export function roundedScaleMaximum(value: number): number {
  if (!Number.isFinite(value) || value <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const normalized = value / magnitude;
  const step = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return step * magnitude;
}

export function burnstreamWindow<T>(items: T[], capacity: 20 | 50): T[] {
  return items.slice(-capacity);
}

export const BURNSTREAM_PLAYBACK_SECONDS = 1.8;
export const BURNSTREAM_BUFFER_BLOCKS = 10;
export const BURNSTREAM_BAR_REVEAL_DELAY_SECONDS = 0.25;

export function observedBlockCadenceSeconds(
  items: Array<{ timestamp: string }>,
  fallback = BURNSTREAM_PLAYBACK_SECONDS,
): number {
  const timestamps = items
    .map((item) => new Date(item.timestamp).getTime())
    .filter(Number.isFinite)
    .sort((left, right) => left - right)
    .slice(-41);
  const intervals = timestamps
    .slice(1)
    .map((timestamp, index) => (timestamp - timestamps[index]) / 1_000)
    .filter((seconds) => seconds >= 0.6 && seconds <= 4);
  if (intervals.length < 3) return fallback;
  intervals.sort((left, right) => left - right);
  const middle = Math.floor(intervals.length / 2);
  const median = intervals.length % 2
    ? intervals[middle]
    : (intervals[middle - 1] + intervals[middle]) / 2;
  return Math.max(1.2, Math.min(2.4, median));
}

export function queueCorrectedCadenceSeconds(
  cadence: number,
  queueDepth: number,
  targetDepth = BURNSTREAM_BUFFER_BLOCKS,
): number {
  const pressure = (queueDepth - targetDepth) / Math.max(1, targetDepth);
  // A shallow reservoir should decelerate gently before it can run dry. The
  // upper bound remains close to Flare's observed cadence while absorbing the
  // jitter from confirmed database batches.
  const correction = Math.max(0.94, Math.min(1.12, 1 - pressure * 0.14));
  return cadence * correction;
}

export interface BurnstreamPlaybackSeed<T> {
  visible: T[];
  pending: T[];
}

export function seedBurnstreamPlayback<T>(
  items: T[],
  capacity: 20 | 50,
  bufferSize = BURNSTREAM_BUFFER_BLOCKS,
): BurnstreamPlaybackSeed<T> {
  const recent = items.slice(-(capacity + bufferSize));
  // Never make a partially populated chart emptier just to create a delay.
  // Once the visible window is full, reserve the newest confirmed blocks for
  // the fixed-rate player so batched ingestion is presented as a live cadence.
  const pendingCount = Math.min(bufferSize, Math.max(0, recent.length - capacity));
  return {
    visible: recent.slice(0, recent.length - pendingCount),
    pending: recent.slice(recent.length - pendingCount),
  };
}

export function initialBurnstreamSlots<T>(items: T[], capacity: 20 | 50): Array<T | null> {
  const visible = burnstreamWindow(items, capacity);
  return [
    ...Array<T | null>(capacity - visible.length).fill(null),
    ...visible,
    null,
  ];
}

export function stageBurnstreamBlock<T>(slots: Array<T | null>, next: T, capacity: 20 | 50): Array<T | null> {
  return [...slots.slice(0, capacity), next];
}

export function completeBurnstreamStep<T>(staged: Array<T | null>): Array<T | null> {
  return [...staged.slice(1), null];
}
